/**
 * Rule-Based Formula & Lookup Evaluator for Mini Printer
 * Handles VLOOKUP, XLOOKUP, INDEX/MATCH, and basic math formulas across Excel sheets.
 */

import type { SheetData } from "@/types/excel";

export interface FormulaContext {
  sheets: SheetData[];
  currentRow?: Record<string, unknown>;
}

/**
 * Clean sheet name from quotes e.g. "'MÃ TUYẾN'!" -> "MÃ TUYẾN"
 */
function cleanSheetName(raw: string): string {
  return raw.replace(/^['"]/, "").replace(/['"]$/, "").trim();
}

/**
 * Extract sheet name and column target from a range reference like DATA!A:D or 'MÃ TUYẾN'!A:B
 */
function parseSheetRangeRef(ref: string): { sheetName: string; rangeStr: string } {
  const parts = ref.split("!");
  if (parts.length === 2) {
    return {
      sheetName: cleanSheetName(parts[0]),
      rangeStr: parts[1].trim(),
    };
  }
  return { sheetName: "", rangeStr: ref.trim() };
}

/**
 * Evaluate a VLOOKUP formula e.g., VLOOKUP(C2, DATA!A:D, 3, FALSE) or VLOOKUP(C2, 'MÃ TUYẾN'!A:B, 2, 0)
 */
export function evaluateVlookup(
  formula: string,
  context: FormulaContext
): string | number | null {
  // Regex pattern for VLOOKUP(key, sheet!range, colIndex, [approx])
  const vlookupRegex = /VLOOKUP\(\s*([^,]+)\s*,\s*([^,]+)\s*,\s*(\d+)(?:\s*,\s*([^)]+))?\s*\)/i;
  const match = vlookupRegex.exec(formula);
  if (!match) return null;

  const rawLookupVal = match[1].trim();
  const rawRange = match[2].trim();
  const colIdx = parseInt(match[3], 10);

  if (isNaN(colIdx) || colIdx < 1) return null;

  // Resolve lookup value (either from currentRow or literal value)
  let lookupKey = rawLookupVal.replace(/^['"]/, "").replace(/['"]$/, "");

  if (context.currentRow && rawLookupVal in context.currentRow) {
    lookupKey = String(context.currentRow[rawLookupVal] ?? "");
  } else if (context.currentRow) {
    // Check if rawLookupVal refers to an Excel column header or cell reference
    const foundVal = Object.entries(context.currentRow).find(
      ([k]) => k.toLowerCase() === rawLookupVal.toLowerCase()
    );
    if (foundVal && foundVal[1] != null) {
      lookupKey = String(foundVal[1]);
    }
  }

  if (!lookupKey) return null;

  // Parse target sheet
  const { sheetName } = parseSheetRangeRef(rawRange);
  const targetSheet = context.sheets.find(
    (s) => s.name.toLowerCase() === sheetName.toLowerCase()
  );

  if (!targetSheet || targetSheet.rows.length === 0 || targetSheet.headers.length === 0) {
    return null;
  }

  // First column of target sheet is key column
  const keyHeader = targetSheet.headers[0];
  const valHeader = targetSheet.headers[colIdx - 1] ?? targetSheet.headers[targetSheet.headers.length - 1];

  if (!keyHeader || !valHeader) return null;

  const normalizedLookupKey = lookupKey.trim().toLowerCase();

  // Search target sheet rows
  const matchedRow = targetSheet.rows.find((r) => {
    const cellVal = r[keyHeader];
    return cellVal != null && String(cellVal).trim().toLowerCase() === normalizedLookupKey;
  });

  if (matchedRow && valHeader in matchedRow) {
    const result = matchedRow[valHeader];
    return result != null ? (typeof result === "number" ? result : String(result)) : null;
  }

  return null;
}

/**
 * Evaluate XLOOKUP formula e.g. XLOOKUP(key, DATA!A:A, DATA!C:C)
 */
export function evaluateXlookup(
  formula: string,
  context: FormulaContext
): string | number | null {
  const xlookupRegex = /XLOOKUP\(\s*([^,]+)\s*,\s*([^,]+)\s*,\s*([^,\)]+)(?:\s*,\s*([^)]+))?\s*\)/i;
  const match = xlookupRegex.exec(formula);
  if (!match) return null;

  const rawLookupVal = match[1].trim();
  const rawKeyRange = match[2].trim();
  const rawReturnRange = match[3].trim();

  let lookupKey = rawLookupVal.replace(/^['"]/, "").replace(/['"]$/, "");
  if (context.currentRow && rawLookupVal in context.currentRow) {
    lookupKey = String(context.currentRow[rawLookupVal] ?? "");
  }

  const { sheetName: keySheetName } = parseSheetRangeRef(rawKeyRange);
  const { sheetName: retSheetName } = parseSheetRangeRef(rawReturnRange);

  const sheetName = keySheetName || retSheetName;
  const targetSheet = context.sheets.find(
    (s) => s.name.toLowerCase() === sheetName.toLowerCase()
  );

  if (!targetSheet || targetSheet.rows.length === 0) return null;

  const keyHeader = targetSheet.headers[0];
  const valHeader = targetSheet.headers[1] ?? targetSheet.headers[0];

  const matchedRow = targetSheet.rows.find((r) => {
    const cellVal = r[keyHeader];
    return cellVal != null && String(cellVal).trim().toLowerCase() === lookupKey.trim().toLowerCase();
  });

  if (matchedRow) {
    const res = matchedRow[valHeader];
    return res != null ? (typeof res === "number" ? res : String(res)) : null;
  }

  return null;
}

/**
 * Main Formula Evaluator Entrypoint
 */
export function evaluateFormula(
  formula: string,
  context: FormulaContext
): string | number | null {
  if (!formula || !formula.startsWith("=")) return null;

  const cleanFormula = formula.substring(1).trim();

  if (/^VLOOKUP/i.test(cleanFormula)) {
    return evaluateVlookup(cleanFormula, context);
  }

  if (/^XLOOKUP/i.test(cleanFormula)) {
    return evaluateXlookup(cleanFormula, context);
  }

  return null;
}
